import { CallDetails, CallStatus, NWayMember, ParkedCall } from '@missionlabs/browser-calling'
import { DirectoryEntry, TeamVmNotificationPayload } from '@missionlabs/types'
import { isFAC } from '@missionlabs/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type Call = {
    alphaTag?: string
    callTraceID: string
    clientName?: string
    companyName?: string
    contactName?: string
    direction?: 'inbound' | 'outbound'
    displayName?: string
    duration?: number
    isTransfer?: boolean
    localNumber?: string
    muted?: boolean
    nWayMembers?: NWayMember[]
    pageGroupName?: string
    recordingState?: 'ON' | 'OFF' | 'PAUSED'
    remoteNumber: string
    startTimestamp?: number
    status: CallStatus
    teamID?: string
    teamName?: string
    transferToContact?: DirectoryEntry
    transferType?: TransferType
    transfereeContact?: DirectoryEntry
    transfereeNumber?: string
    transfererCompanyName?: string
    transfererContactName?: string
    transfererNumber?: string
    userGroupName?: string
    userID?: string
}

export type TransferType = 'attended' | 'blind'

type UpdatedCall = Omit<Call, 'callTraceID' | 'webSocket'>

export type CallState = {
    calls: Call[]
    parkedCalls: ParkedCall[]
    callGroupMessages: TeamVmNotificationPayload[]
    // circleloop
    presentationNumber?: string
    focusedCallTraceID: string | undefined
}

const initialState: CallState = {
    calls: [],
    parkedCalls: [],
    callGroupMessages: [],
    focusedCallTraceID: undefined
}

export const callSlice = createSlice({
    name: 'callSlice',
    initialState,
    reducers: {
        focusCall: (state, action: PayloadAction<{ callTraceID: string | undefined }>) => {
            state.focusedCallTraceID = action.payload?.callTraceID
        },
        addCall: (state, action: PayloadAction<Call>) => {
            state.calls.push(action.payload)
        },
        removeCall: (state, action: PayloadAction<{ callTraceID: string }>) => {
            state.calls = state.calls.filter(
                call => call.callTraceID !== action.payload?.callTraceID
            )
        },
        updateCall(
            state,
            action: PayloadAction<{ updatedCall: UpdatedCall; callTraceID: string }>
        ) {
            const { callTraceID, updatedCall } = action.payload
            state.calls = state.calls.map(call =>
                call.callTraceID === callTraceID
                    ? {
                          ...call,
                          ...updatedCall,
                          startTimestamp: call.startTimestamp ?? updatedCall.startTimestamp
                      }
                    : call
            )
        },
        updateParkedCalls(state, action: PayloadAction<{ parkedCalls: ParkedCall[] }>) {
            state.parkedCalls = action.payload.parkedCalls
        },
        updateCallGroupMessages(state, action: PayloadAction<TeamVmNotificationPayload>) {
            state.callGroupMessages.push({ ...action.payload, callTraceID: crypto.randomUUID() })
        },
        updateDetails(
            state,
            action: PayloadAction<{ callTraceID: string; callDetails: CallDetails }>
        ) {
            const { callTraceID, callDetails } = action.payload
            state.calls = state.calls.map(call =>
                call.callTraceID === callTraceID
                    ? {
                          ...call,
                          ...callDetails
                      }
                    : call
            )
        },
        setPresentationNumber(state, action: PayloadAction<string | undefined>) {
            state.presentationNumber = action.payload
        }
    }
})

export type CallSliceRootState = {
    callSlice: ReturnType<typeof callSlice.getInitialState>
}

export const selectIncomingCalls = (state: CallSliceRootState) => {
    return state.callSlice.calls.filter(
        call => call.status === 'RINGING_LOCAL' && call.direction === 'inbound'
    )
}

export const selectConnectedCalls = (state: CallSliceRootState) => {
    return state.callSlice.calls.filter(call => ['CONNECTED', 'ON_HOLD'].includes(call.status))
}

export const selectConnectedCall = (state: CallSliceRootState) => {
    return state.callSlice.calls.find(call => call.status === 'CONNECTED')
}

export const selectConnectedCallOrFAC = (state: CallSliceRootState) => {
    const canSendDtmfInStatus = (status: CallStatus) =>
        ['CONNECTED', 'TRANSFERRING', 'ANSWERED'].includes(status)

    const focusedCall = state.callSlice.calls.find(
        call =>
            call.callTraceID === state.callSlice.focusedCallTraceID &&
            canSendDtmfInStatus(call.status)
    )
    if (focusedCall) return focusedCall

    const transferring = state.callSlice.calls.find(call => call.status === 'TRANSFERRING')
    if (transferring) return transferring

    const connected = state.callSlice.calls.find(call => call.status === 'CONNECTED')
    if (connected) return connected

    const answered = state.callSlice.calls.find(call => call.status === 'ANSWERED')
    if (answered) return answered

    const fac = state.callSlice.calls.find(call => isFAC(call.remoteNumber))
    if (fac) return fac
}

export const selectAnsweredCalls = (state: CallSliceRootState) => {
    return state.callSlice.calls.filter(call => call.status === 'ANSWERED')
}

export const selectCallStatus = (state: CallSliceRootState, callTraceID: string): CallStatus => {
    const call = state.callSlice.calls.find(call => call.callTraceID === callTraceID)
    return call?.status || 'NO_CALL'
}

export const selectCall = (state: CallSliceRootState, callTradeID: string) =>
    state.callSlice.calls.find(call => call.callTraceID === callTradeID)

export const selectCalls = (state: CallSliceRootState) => state.callSlice.calls

export const selectParkedCalls = (state: CallSliceRootState) => state.callSlice.parkedCalls

export const selectCallGroupMessages = (state: CallSliceRootState) =>
    state.callSlice.callGroupMessages

export const selectPresentationNumber = (state: CallSliceRootState) =>
    state.callSlice.presentationNumber

export const selectFocusedCall = (state: CallSliceRootState) => {
    return state.callSlice.calls.find(
        call => call.callTraceID === state.callSlice.focusedCallTraceID
    )
}

export const {
    addCall,
    removeCall,
    updateCall,
    updateDetails,
    updateParkedCalls,
    updateCallGroupMessages,
    setPresentationNumber,
    focusCall
} = callSlice.actions
